/**
 * @license
 * Copyright 2017 Google Inc.
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *      http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */

/**
 * @file Facilities for laying out multiple LayerGroupViewer instances.
 */

import { vec3 } from 'gl-matrix';
import debounce from 'lodash/debounce';
import { LayerListSpecification, LayerSubsetSpecification, ManagedUserLayer } from 'neuroglancer/layer';
import { getViewerDropEffect, hasViewerDrag, LayerGroupViewer, viewerDragType } from 'neuroglancer/layer_group_viewer';
import { popDragStatus, pushDragStatus } from 'neuroglancer/ui/drag_and_drop';
import { DropLayers, endLayerDrag, getDropLayers, getLayerDragInfo, updateLayerDropEffect } from 'neuroglancer/ui/layer_drag_and_drop';
import { Borrowed, RefCounted } from 'neuroglancer/util/disposable';
import { removeFromParent } from 'neuroglancer/util/dom';
import { getDropEffect, setDropEffect } from 'neuroglancer/util/drag_and_drop';
import { parseArray, verifyObject, verifyObjectProperty, verifyString } from 'neuroglancer/util/json';
import { NullarySignal } from 'neuroglancer/util/signal';
import { Trackable } from 'neuroglancer/util/trackable';
import { arrayObject, Viewer } from 'neuroglancer/viewer';
import { viewer, viewer as _layoutviewer, allToolsButton } from 'src/main';
import { colorArr } from './annotation/user_layer';
import './layer_groups_layout.less';
import { getBaseObjectColor, SegmentationDisplayState } from './segmentation_display_state/frontend';
import { makeElement, _checkColor } from './ui/layer_gene_table_tab';
import { serializeColor } from './util/color';
import { Uint64 } from './util/uint64';
import i18n from './i18n/i18n';
import { SelectView } from './widget/select_view';
import { PreprocessedSegmentPropertyMap } from './segmentation_display_state/property_map';
import { SegmentationUserLayerDisplayState } from './segmentation_user_layer';

interface LayoutComponent extends RefCounted {
  element: HTMLElement;
  changed: NullarySignal;
  toJSON(): any;
}

const layoutComponentContainerSymbol = Symbol('layoutComponentContainer');
// let segmentLabel = '';

 /* Container for a LayoutComponent.  The contained LayoutComponent may change.
 */
export class LayoutComponentContainer extends RefCounted {
  changed = new NullarySignal();
  private componentValue: LayoutComponent;
  private initColorScaleTimer: any;

  private unsetComponent() {
    const oldComponent = this.componentValue;
    if (oldComponent !== undefined) {
      oldComponent.changed.remove(this.changed.dispatch);
      this.element.removeChild(oldComponent.element);
      oldComponent.dispose();
    }
  }

  get component() {
    return this.componentValue;
  }

  private setComponent(component: LayoutComponent) {
    this.unsetComponent();
    this.componentValue = component;
    component.changed.add(this.changed.dispatch);
    this.element.appendChild(component.element);

    if (component instanceof LayerGroupViewer) {
      const {layerManager} = component;
      const scheduleMaybeDelete = component.registerCancellable(debounce(() => {
        if (layerManager.managedLayers.length === 0) {
          this.dispose();
        }
      }, 0));
      component.registerDisposer(layerManager.layersChanged.add(() => {
        if (layerManager.managedLayers.length === 0) {
          scheduleMaybeDelete();
        }
      }));
      scheduleMaybeDelete();
    } else if (component instanceof StackLayoutComponent) {
      const scheduleMaybeDelete = component.registerCancellable(debounce(() => {
        const {length} = component;
        if (length === 0 && this.parent !== undefined) {
          this.dispose();
        } else if (length === 1) {
          const childComponent = component.get(0).component;
          let spec: any;
          if (this.parent === undefined && childComponent instanceof LayerGroupViewer) {
            spec = childComponent.layout.specification.toJSON();
            childComponent.viewerNavigationState.copyToParent();
            const childManagedLayers = childComponent.layerManager.managedLayers;
            const layersToKeep = new Set(childManagedLayers);
            const {layerSpecification} = childComponent;
            // Retain only layers that are part of the layer group, or are archived.
            layerSpecification.rootLayers.filter(
                layer => layersToKeep.has(layer) || layer.archived);
            // Permute the non-archived layers to match the order in the layer group.
            const childLayerIndices: number[] = [];
            const {managedLayers: rootManagedLayers} = layerSpecification.rootLayers;
            for (let i = 0, count = rootManagedLayers.length; i < count; ++i) {
              if (layersToKeep.has(rootManagedLayers[i])) {
                childLayerIndices.push(i);
              }
            }
            for (let i = 0, count = childManagedLayers.length; i < count; ++i) {
              rootManagedLayers[childLayerIndices[i]] = childManagedLayers[i];
            }
            layerSpecification.rootLayers.layersChanged.dispatch();
          } else {
            spec = childComponent.toJSON();
          }
          this.setSpecification(spec);
        }
      }, 0));
      component.registerDisposer(component.changed.add(() => {
        if (component.length < 2) {
          scheduleMaybeDelete();
        }
      }));
      scheduleMaybeDelete();
    }
    this.changed.dispatch();
  }


  element = document.createElement('div');
  div = document.createElement('div');
  colorScale = document.createElement('ul');
  colorScaleCanvas = document.createElement('canvas');
  colorScaleCanvasCtx: CanvasRenderingContext2D | null;
  colorScaleGridInfo: Array<{height:number,label:string}> =[];
  proteinLegendUl = makeElement('ul',['protein-legend-ul'])
  clusterLegendUl = makeElement('ul',['cluster-legend-ul'])
  viewers: Viewer;
  constructor(public viewer: Viewer, spec: any, public parent: StackLayoutComponent|undefined) {
    super();
    this.viewers = viewer;
    const {element, div, colorScale} = this;
    element.style.display = 'flex';
    element.style.flex = '1';
    element.style.alignItems = 'stretch';
    element.style.position = 'relative';
    div.classList.add('colorBarScale-container');
    colorScale.classList.add('colorBarScale');
    colorScale.style.margin = '0';
    (<any>element)[layoutComponentContainerSymbol] = this;
    this.setSpecification(spec);
    this.colorScaleCanvasCtx = this.colorScaleCanvas.getContext("2d")

    interface DropZone {
      element: HTMLElement;
      direction: 'row'|'column';
      orientation: 'left'|'right'|'top'|'bottom';
    }

    const dropZones: DropZone[] = [];
    const makeDropZone = (name: 'left'|'right'|'top'|'bottom') => {
      const dropZone = document.createElement('div');
      dropZone.className = 'neuroglancer-layout-split-drop-zone';
      let direction: 'row'|'column';
      dropZone.style[name] = '0';
      switch (name) {
        case 'left':
        case 'right':
          direction = 'row';
          dropZone.style.width = '10px';
          dropZone.style.height = '100%';
          break;
        case 'top':
        case 'bottom':
          direction = 'column';
          dropZone.style.height = '10px';
          dropZone.style.width = '100%';
          break;
      }
      dropZone.style.display = 'none';
      dropZones.push({element: dropZone, direction: direction!, orientation: name});
      element.appendChild(dropZone);
      element.appendChild(div);
      setupDropZone(
          dropZone, this.viewer.layerSpecification,
          () => <LayerGroupViewer>(this.split(name).newContainer.component),
          direction === 'row' ? 'column' : 'row');
    };
    makeDropZone('left');
    makeDropZone('right');
    makeDropZone('top');
    makeDropZone('bottom');

    let dropZonesVisible = false;
    element.addEventListener('dragenter', (event: DragEvent) => {
      if (dropZonesVisible) {
        return;
      }
      if (getLayerDragInfo(event) === undefined) {
        return;
      }
      dropZonesVisible = true;
      for (const {element: dropZone, direction, orientation} of dropZones) {
        if (parent !== undefined && direction === parent.direction) {
          if (((orientation === 'left' || orientation === 'top') && parent.get(0) !== this) ||
              ((orientation === 'bottom' || orientation === 'right') &&
               parent.get(parent.length - 1) !== this)) {
            continue;
          }
        }
        const {component} = this;
        if (component instanceof StackLayoutComponent && component.direction === direction) {
          continue;
        }
        dropZone.style.display = 'block';
      }
    }, true);

    element.addEventListener('drop', (_event: DragEvent) => {
      if (!dropZonesVisible) {
        return;
      }
      dropZonesVisible = false;
      for (const {element: dropZone} of dropZones) {
        dropZone.style.display = 'none';
      }
    }, /*capture=*/ true);
    element.addEventListener('dragleave', (event: DragEvent) => {
      const {relatedTarget} = event;
      if (!dropZonesVisible) {
        return;
      }
      if (relatedTarget instanceof HTMLElement && this.element.contains(relatedTarget)) {
        return;
      }
      dropZonesVisible = false;
      for (const {element: dropZone} of dropZones) {
        dropZone.style.display = 'none';
      }
    }, true);
  }
  
  toJSON() {
    return this.component.toJSON();
  }

  initColorScale(){
    clearTimeout(this.initColorScaleTimer);
    this.initColorScaleTimer = setTimeout(()=> {
    // colorbar, ledgend of image, ledgen of cluster, 有cluster优先展示cluster
      if(_layoutviewer){
        const alllayer = this.viewer.layerManager.managedLayers;
        let segInd = 0;
        let cellvalue:any;
        let has_cluster_image = 'nolegend';
        viewer.showColorbar.value = false;
        viewer.showLegend.value = false;
        this.colorScale.style.display = 'none';
        alllayer.forEach((item:any, index: number)=>{
          if(item?.heatmap && Boolean(item.visible) && !viewer.proteinMergeColor.value){
            // 筛选热图展示color bar
            viewer.showColorbar.value = true;
          }else if((item.name.includes('cluster') && item.visible) || (item.name.includes('UMAP') && item.visible)){
            viewer.showLegend.value = true;
            segInd = index;
            cellvalue = {
              value: item.layer.dataSources[1]?.loadState_?.dataSource?.subsources[0].subsource.segmentPropertyMap.inlineProperties,
            displaystate: item.layer.displayState as PreprocessedSegmentPropertyMap,
            }
            if(cellvalue.value.properties?.[0]?.values?.length > 0){
              has_cluster_image = has_cluster_image +'cluster';
              viewer.isLegendOfImage.value = false;
            }
          }else if(item.genetype.includes('protein') && item.visible){
            has_cluster_image = has_cluster_image + 'image';
            viewer.showLegend.value = true;
            viewer.isLegendOfImage.value = true;
          }
        });
        if(has_cluster_image.includes('cluster')){
          if (cellvalue?.value) {
            const json = viewer.state.toJSON();
            const segments = json.layers[segInd].segments;
            const _new_segments:any = [];
            const has_default_info = cellvalue.value.properties?.[0]?.values?.[0]?.includes('cluster');
            cellvalue.value.properties?.[0]?.values?.forEach((item:any,index: number)=>{
              let _segments = segments && has_default_info ? segments.map((item:any)=> (Number(item) - 1 ).toString()) : segments;
              if(_segments && _segments.includes(index + '')){
                let _label = item;
                _new_segments.push({ids: index + 1, labels: _label})
              }
            });
            viewer.allDataSource.segmentlist = {
              order: viewer.allDataSource.segmentlist.order,
              list: segments ? _new_segments : cellvalue?.value?.properties?.[0]?.values?.map((item: arrayObject, index: number)=> { return {"ids": (index+1), labels: item} }) ?? [],
              label: viewer.allDataSource.segmentlist.label
            }
            this.initCellBinLegendFormatter(sortBySegmentList(viewer.allDataSource.segmentlist.list, viewer.allDataSource.segmentlist.order), cellvalue.displaystate);
            document.getElementsByClassName('neuroglancer-viewer-legend-item')[0].appendChild(this.proteinLegendUl as HTMLElement);
          }
        }else if(has_cluster_image.includes('image')){
          this.proteinLegendFormatter(this.viewer.layerManager.managedLayers);
          document.getElementsByClassName('neuroglancer-viewer-legend-item')[0].appendChild(this.proteinLegendUl as HTMLElement);
        }else if(has_cluster_image === 'nolegend'){
          const dom = document.getElementsByClassName('neuroglancer-viewer-legend-item')[0] as HTMLElement;
          dom.style.display = 'none';
          viewer.showLegend.value = false;
        }
        this.changeLegendFormatter(cellvalue?.displaystate, has_cluster_image)
        if(viewer.showColorbar.value){
        if(viewer.color_scale[allToolsButton.genelistFile]){
          this.colorScale.style.display = 'block';
            this.colorBarScale(viewer.color_scale[allToolsButton.genelistFile].maxValue, this.viewer.allDataSource.colorscale);
          }
        }
      document.getElementsByClassName('neuroglancer-viewer-scalebar')[0].appendChild(this.colorScale as HTMLElement);
    }
    },200)
  }

  setSpecification(spec: any) {
    this.setComponent(makeComponent(this, spec));
  }

  static getFromElement(element: Element): LayoutComponentContainer {
    return (<any>element)[layoutComponentContainerSymbol];
  }

  disposed() {
    this.unsetComponent();
    (<any>this).componentValue = undefined;
    super.disposed();
  }

  split(side: 'left'|'top'|'bottom'|'right'):
      {newContainer: LayoutComponentContainer, existingContainer: LayoutComponentContainer} {
    const newComponentSpec: any = {
      type: 'viewer',
    };

    const {parent} = this;
    if (parent !== undefined) {
      if ((side === 'left' && parent.direction === 'row') ||
          (side === 'top' && parent.direction === 'column')) {
        return {newContainer: parent.insertChild(newComponentSpec, this), existingContainer: this};
      } else if (
          (side === 'right' && parent.direction === 'row') ||
          (side === 'bottom' && parent.direction === 'column')) {
        return {newContainer: parent.insertChild(newComponentSpec), existingContainer: this};
      }
    }

    let existingComponentSpec: any;
    const existingComponent = this.component;
    if (existingComponent instanceof SingletonLayerGroupViewer) {
      existingComponentSpec = existingComponent.layerGroupViewer.toJSON();
    } else {
      existingComponentSpec = existingComponent.toJSON();
    }
    let spec: any;
    let newIndex: number;
    const direction = side === 'left' || side === 'right' ? 'row' : 'column';
    switch (side) {
      case 'left':
      case 'top':
        spec = {type: direction, children: [newComponentSpec, existingComponentSpec]};
        newIndex = 0;
        break;
      case 'right':
      case 'bottom':
        spec = {type: direction, children: [existingComponentSpec, newComponentSpec]};
        newIndex = 1;
        break;
    }
    this.setSpecification(spec!);
    const stackComponent = <StackLayoutComponent>this.component;
    return {
      newContainer: stackComponent.get(newIndex!),
      existingContainer: stackComponent.get(1 - newIndex!)
    };
  }

  updateColorScaleImg(zoom = 1){
    var canvas = this.colorScaleCanvas;
    var context = this.colorScaleCanvasCtx;
    if (context) {
      canvas.width = 80 * zoom;
      canvas.height = 110 * zoom;
      let curColorArr;
      if( viewer.allDataSource.colorscale.color.length > 0){
        curColorArr = viewer.allDataSource.colorscale.color;
      } else {
        curColorArr = colorArr[0].colormap;
      }
      const textArr = this.colorScaleGridInfo;

      var linearGradient = context.createLinearGradient(10 * zoom, 110 * zoom, 0, 0);

      let offset = 0;
      let step = 1 / curColorArr.length;
      curColorArr.forEach((item: string) => {
        linearGradient.addColorStop(offset, item);
        offset += step;
      })
      context.clearRect(0, 0, canvas.width, canvas.height);
      context.fillStyle = linearGradient;
      context.fillRect(0, 10 * zoom, 20 * zoom, 110 * zoom);
      let textPosYOffset = 10.5 * zoom;
      let lineStartPosX = 10 * zoom;
      let lineEndPosX = 20 * zoom;
      let txtPosX = 30 * zoom;
      context.strokeStyle = '#000000'
      context.lineWidth = zoom;

      context.font = `${12 * zoom}px MicrosoftYaHei`;
      context.fillStyle = "#79d0f3";
      textArr.forEach(item => {
        if (item.label) {
          context!.moveTo(lineStartPosX, textPosYOffset);
          context!.lineTo(lineEndPosX, textPosYOffset);
          context!.stroke();
          context!.fillText(item.label, txtPosX, (textPosYOffset + 3.5 * zoom));
        }
        textPosYOffset += item.height * zoom;
      })
      return canvas;
    }
    return undefined;
  }
  // 连续colorbar
  colorBarScale(value: string | number, color?:any){
    this.colorScale.innerHTML = '';   // 
    this.colorScale.style.overflowY = 'visible';
    this.div.style.maxHeight = 'none';
    this.div.style.overflowY = 'visible';
    this.div.style.paddingRight = '0';
    if(value > '0'){
      // 处理平均每个格子单位量
      let unit = Math.ceil((Number(value) - viewer.color_scale[allToolsButton.genelistFile].minValue)/4);
      unit = getNearCale(Number(unit));
      //  获取单个格子像素
      let unitCalePX = Number( unit ) / (Number(Number(value) - viewer.color_scale[allToolsButton.genelistFile].minValue) / 100);
      let len = Math.ceil( (Number(value) - viewer.color_scale[allToolsButton.genelistFile].minValue) / unit);
      len = (Number(value) - viewer.color_scale[allToolsButton.genelistFile].minValue) % unit ? len : len + 1;
      let unit_mid_count = unitCalePX / unit;   // 获取单位像素的midcount数目
      const min_gezi_px = unit_mid_count * ( Math.ceil( viewer.color_scale[allToolsButton.genelistFile].minValue / unit ) * unit - viewer.color_scale[allToolsButton.genelistFile].minValue );
      const max_gezi_px = unit_mid_count * ( viewer.color_scale[allToolsButton.genelistFile].maxValue - Math.floor(viewer.color_scale[allToolsButton.genelistFile].maxValue / unit ) * unit);
      this.colorScaleGridInfo = [];
      for(let i = len; i > 0; i--){
        const li = makeElement('li', ['colorBarScaleli'], {});
        const liHeight = i === len ? max_gezi_px : i === 1 ? min_gezi_px : unitCalePX;
        li.style.height = liHeight + 'px';
        let text = i === len ? max_gezi_px !== unitCalePX ? '' : formatterUnit( Number(value),Number(unit) ) : formatterUnit( (i + Math.floor(viewer.color_scale[allToolsButton.genelistFile].minValue / unit)) * Number(unit), Number(unit) );
        this.colorScaleGridInfo.push( {height:liHeight,label:text} );
        const a = makeElement('a',[],{}, ( text.toString() ));
        li.appendChild(a);
        this.colorScale.appendChild(li);
      }
      this.colorScale.appendChild(makeElement('a',[],{}, min_gezi_px !== unitCalePX ? '' : viewer.color_scale[allToolsButton.genelistFile].minValue.toString()));
      this.colorScale.style.height = '100px';
      this.colorScale.style.width = '20px';
      const heatmap_genecolor = viewer.layerManager.managedLayers.filter((item:any)=>item.heatmap && item.genename === allToolsButton.genelistFile)[0];
      if(heatmap_genecolor?.genecolor){
        const cuur_color_colorbar = colorArr.filter(item=>item.colorName === heatmap_genecolor.genecolor);
        this.colorScale.style.backgroundImage = color.dir ? 'linear-gradient(to '+ color.dir + [...cuur_color_colorbar[0].colormap] + ')' : 'linear-gradient(to top, #0c3383,#005ea3, #0a88ba, #00c199, #f2d338, #F6b132, #f28f38, #e76124, #d91e1e)';
        (document.getElementsByClassName('colorBarScale-container')[0] as HTMLElement).style.display = 'block';
        if(document.getElementsByClassName('colorBarScale')[0]){ let dom = document.getElementsByClassName('colorBarScale')[0]; dom.remove()}
      }
    }else{
      (document.getElementsByClassName('colorBarScale-container')[0] as HTMLElement).style.display = 'none';
    }
  }
  // 蛋白图例
  proteinLegendFormatter(arr: any){
    this.proteinLegendUl.innerHTML = '';   // 每次调用前先清除内部
    this.div.style.maxHeight = '200px';
    this.div.style.overflowY = 'auto';
    this.div.style.paddingRight = '6px';
    let flag = false;
    arr.forEach((item:any)=>{
      if(item.genetype.includes('proteinimage') && item.visible){
        const li = makeElement('li', ['protein-legend-li-'+item.genename], {'style':'display: flex;padding: 0 1rem'});
        const span = makeElement('span',['color-widget-'+item.genename],{'style': 'flex-shrink: 0;margin: 4px;width: .625rem;height:.625rem;border-radius: 50%;background: ' + (item.genecolor.includes('#') ? item.genecolor : '#' + item.genecolor)});
        const name = item.genetype === 'proteinimage_image' ? getImagename(item.genename) : item.genename;
        const text = makeElement('span',['legend-span'],{'style':'margin: auto auto auto 0;text-decoration: none;color: #fff'},`${name}`);
        li.appendChild(span);
        li.appendChild(text);
        this.proteinLegendUl.appendChild(li);
        flag = true;
      }
    });
    if(document.getElementsByClassName('legendUl')[0]){ let dom = document.getElementsByClassName('legendUl')[0]; dom.remove()};
    const dom = document.getElementsByClassName('neuroglancer-viewer-legend-item')[0] as HTMLElement;
    if(flag){
      dom.appendChild(this.proteinLegendUl as HTMLElement);
    }else{
      if(!viewer.layerManager.getLayerVisible('cluster')) dom.style.display = 'none';
    }
  }
  // 聚类图例
  initCellBinLegendFormatter(arr: any = [], displayState: SegmentationUserLayerDisplayState){
    let clusterlayer_name:any = this.viewer.layerManager.managedLayers.filter((item:ManagedUserLayer)=> (item.name.includes('cluster') && item.visible) || (item.name.includes('UMAP') && item.visible) )
    clusterlayer_name = clusterlayer_name.length > 0 ? clusterlayer_name[0].genefile : undefined;
    const self_newlabel = viewer.binSize.value === 'cellbin' ? viewer.self_newlabel[clusterlayer_name] ?? {} : viewer.bin200_self_newlabel[clusterlayer_name]?.[viewer.binSize.value as string] ?? {};
    const segmentColors = viewer.binSize.value === 'cellbin' ? viewer.self_newlabel_color[clusterlayer_name] ?? {} : viewer.bin200_self_newlabel_color[clusterlayer_name]?.[viewer.binSize.value as string] ?? {};
    this.proteinLegendUl.innerHTML = '';   // 每次调用前先清除内部
    arr.forEach((item:any)=>{
      const propIndex = item.ids - 1;
      const {ids} = (displayState.segmentPropertyMap.value as PreprocessedSegmentPropertyMap).segmentPropertyMap.inlineProperties!;
      // const low_ids = arr[0].ids === 1 ? item.ids - 1 : item.ids;
      const orignColor = getBaseObjectColor(displayState as SegmentationDisplayState, {low: ids[propIndex * 2],high: ids[propIndex * 2 + 1]} as Uint64) as vec3;
      const color = segmentColors && item.ids in segmentColors ? segmentColors[item.ids] : serializeColor(vec3.fromValues(orignColor[0], orignColor[1], orignColor[2]));
      const li = makeElement('li', ['protein-legend-li-'+item.ids], {'style':'display: flex; padding: 0 1rem;'});
      const span = makeElement('span',['color-widget-'+item.ids],{'style': 'flex-shrink: 0;display: inline-block;margin: 0.25rem;width: .625rem;height:.625rem; border-radius: 50%; background:'+ color});
      const input = makeElement('span',['color-widget-a-'+item.ids, 'legend-span'],{'style':'color: #fff;background: none;border:none;width:calc(100% - 1.5rem)'},`${self_newlabel[item.ids] || item.labels}`);
      li.appendChild(span);
      li.appendChild(input);
      this.proteinLegendUl.appendChild(li);
    });
  }
  // 添加图例切换
  changeLegendFormatter(displayState: SegmentationUserLayerDisplayState | undefined, has_cluster_image: string){
    const dom = document.getElementsByClassName('legend-image-cluster')[0] as HTMLElement;
    dom.style.height = '2.5rem';
    dom.style.margin = 'auto';
    const legendContiner = makeElement('div',['binsize-container'],{style: 'margin-top: 0'});
    const ul = makeElement('ul',['el-select-dropdown__list']);
    const imageli = makeElement('li',['el-select-dropdown_item'],{});
    const imagespan = makeElement('span',[],{},`${i18n.legend_image}`);
    const clusterli = makeElement('li',['el-select-dropdown_item'],{});
    const clusterspan = makeElement('span',[],{},`${i18n.legend_cluster}`);
    imageli.appendChild(imagespan);
    clusterli.appendChild(clusterspan);
    legendContiner.appendChild(ul);
    ul.appendChild(imageli);
    ul.appendChild(clusterli);
    const button = new SelectView(null, {
      classes: ['lasso-binsize-view'],
      defaultValue: viewer.isLegendOfImage.value ? i18n.legend_image : i18n.legend_cluster,
      backgroundScheme: 'dark',
      childElement: legendContiner
    });
    dom.innerHTML = '';
    if(displayState &&  /^(?=.*image)(?=.*cluster).*$/.test(has_cluster_image)){
      button.element.style.border = 'none';
      button.element.style.height = '100%';
      viewer.layerManager.getLayerVisible('proteinimage') ? dom.appendChild(button.element) : dom.innerHTML = `${i18n.legend_cluster}`;
      button.element.addEventListener('click',()=>{
        if (button.element.classList.contains('lasso_disabled')) return
        button.element.getAttribute('data-checked') === 'true' ? button.element.appendChild(legendContiner) : button.element.removeChild(legendContiner);
      });
      imageli.addEventListener('click',()=>{
        this.proteinLegendFormatter(this.viewer.layerManager.managedLayers);
        button.setLabel(`${i18n.legend_image}`);
        button.close();
        viewer.isLegendOfImage.value = true;
      });
      clusterli.addEventListener('click',()=>{
        this.initCellBinLegendFormatter(sortBySegmentList(viewer.allDataSource.segmentlist.list, viewer.allDataSource.segmentlist.order), displayState);
        button.setLabel(`${i18n.legend_cluster}`);
        button.close();
        viewer.isLegendOfImage.value = false;
      });
    }else{
      dom.innerHTML = viewer.layerManager.getLayerVisible('proteinimage') ? `${i18n.legend_image}` : `${i18n.legend_cluster}`;
      // viewer.isLegendOfImage.value = viewer.layerManager.getLayerVisible('proteinimage');
    }
  }
}

function getCommonViewerState(viewer: Viewer) {
  return {
    mouseState: viewer.mouseState,
    showAxisLines: viewer.showAxisLines,
    wireFrame: viewer.wireFrame,
    showScaleBar: viewer.showScaleBar,
    scaleBarOptions: viewer.scaleBarOptions,
    showPerspectiveSliceViews: viewer.showPerspectiveSliceViews,
    inputEventBindings: viewer.inputEventBindings,
    visibility: viewer.visibility,
    selectedLayer: viewer.selectedLayer,
    visibleLayerRoles: viewer.visibleLayerRoles,
    navigationState: viewer.navigationState.addRef(),
    perspectiveNavigationState: viewer.perspectiveNavigationState.addRef(),
    crossSectionBackgroundColor: viewer.crossSectionBackgroundColor,
    perspectiveViewBackgroundColor: viewer.perspectiveViewBackgroundColor
  };
}

// 将最近单位格式化
function formatterUnit(value:number, unitValue: number){
  let unitArr = ['', 'k', 'M', 'G', 'T'];
  let unit = Math.floor( (value.toString().length - 1) / 3);   // 获取单位进制
  let isFormatterValue = Math.floor( (unitValue.toString().length - 1) / 3);   // 用于判断是否格式化
  let formatterValue = isFormatterValue > 0 ? unit > 0 ? ( (value / Math.pow(1000,unit)) + unitArr[unit] ) : (value + ''):(value + '');
  return formatterValue;
}

// 获取最近单位
function getNearCale(value: number){
  const scalArr = [1, 2, 4, 5, 10, 15, 20, 30, 50, 100, 200, 300, 500, 1000, 1500, 2000, 5000, 10000, 15000, 18000, 20000, 22000, 25000, 28000, 30000, 35000, 40000, 50000, 100000, 200000, 500000, 1000000, 2000000, 5000000, 10000000];
  let nearCale: number[] = [];
  let scalIndex:number = 0;
  scalArr.forEach((item, index)=>{ 
    if( (value / item) < 1 && nearCale.length < 1){
      nearCale.push( value);
      return;
    }else if((value / item) >= 1){
      nearCale.push(item);
      scalIndex = index;
    } });
    // 判断是否有更接近的单位刻度
  if( (1 - value / scalArr[scalIndex + 1]) < (value / scalArr[scalIndex + 1] - 1) ){
    nearCale.push(scalArr[scalIndex + 1]);
  }
  let len = nearCale.length;
  return nearCale[len - 1]
}

function sortBySegmentList(list: Array<{[K: string]: string | number}>, order: string){
  switch (order){
    case '<id':
      list.sort((a, b)=> (a.ids as number) - (b.ids as number));
      break;
    case '>id':
      list.sort((a, b)=> (b.ids as number) - (a.ids as number));
      break;
    case '<label':
      list.sort((a, b)=> (a.labels as string).localeCompare(b.labels as string));
      break;
    case '>label':
      list.sort((a, b)=> (b.labels as string).localeCompare(a.labels as string));
      break;
    default:
      list;
      break
  }
  return list
}

export class SingletonLayerGroupViewer extends RefCounted implements LayoutComponent {
  layerGroupViewer: LayerGroupViewer;

  constructor(public element: HTMLElement, layout: any, viewer: Viewer) {
    super();
    this.layerGroupViewer = this.registerDisposer(new LayerGroupViewer(
        element,  
        {
          display: viewer.display,
          layerSpecification: viewer.layerSpecification.addRef(),
          ...getCommonViewerState(viewer),
        },

        {
          showLayerPanel: viewer.uiControlVisibility.showLayerPanel,
          showViewerMenu: false,
          showLayerHoverValues: viewer.uiControlVisibility.showLayerHoverValues
        }));

    this.layerGroupViewer.layout.restoreState(layout);
  }

  toJSON() {
    return this.layerGroupViewer.layout.specification.toJSON();
  }

  get changed() {
    return this.layerGroupViewer.layout.changed;
  }
}

function setupDropZone(
    dropZone: HTMLElement, manager: Borrowed<LayerListSpecification>,
    makeLayerGroupViewer: () => Borrowed<LayerGroupViewer>, direction: 'row'|'column') {
  dropZone.addEventListener('dragenter', (event: DragEvent) => {
    const dragInfo = getLayerDragInfo(event);
    if (dragInfo === undefined) {
      return;
    }
    dropZone.classList.add('neuroglancer-drag-over');
  });
  dropZone.addEventListener('dragleave', () => {
    popDragStatus(dropZone, 'drop');
    dropZone.classList.remove('neuroglancer-drag-over');
  });
  dropZone.addEventListener('dragover', (event: DragEvent) => {
    const allowDrag = (info: {dropEffect: string, dropEffectMessage: string}, message: string) => {
      if (info.dropEffectMessage) message += ` (${info.dropEffectMessage})`;
      pushDragStatus(dropZone, 'drop', message);
      event.stopPropagation();
      event.preventDefault();
    };
    if (hasViewerDrag(event)) {
      const info = getViewerDropEffect(event, manager);
      setDropEffect(event, info.dropEffect);
      return;
    }
    if (getLayerDragInfo(event) !== undefined) {
      const info = updateLayerDropEffect(
          event, manager, /*targetIsLayerListPanel=*/ false, /*newTarget=*/ true);
      allowDrag(info, `Drop to ${info.dropEffect} layer as new ${direction}`);
      return;
    }
  });
  dropZone.addEventListener('drop', (event: DragEvent) => {
    dropZone.classList.remove('neuroglancer-drag-over');
    popDragStatus(dropZone, 'drop');
    let dropLayers: DropLayers|undefined;
    let layoutSpec: any;
    if (hasViewerDrag(event)) {
      event.stopPropagation();
      try {
        layoutSpec = JSON.parse(event.dataTransfer!.getData(viewerDragType));
      } catch (e) {
        return;
      }
      dropLayers = getDropLayers(event, manager, {forceCopy: false, newTarget: true});
      if (dropLayers === undefined) return;
    } else {
      dropLayers =
          getDropLayers(event, manager, {forceCopy: getDropEffect() === 'copy', newTarget: true});
      if (dropLayers === undefined) return;
      layoutSpec = dropLayers.layoutSpec;
    }

    if (!dropLayers.initializeExternalLayers(event)) {
      if (!dropLayers.moveSupported) {
        for (const layer of dropLayers.layers.keys()) {
          layer.dispose();
        }
      }
      return;
    }
    event.preventDefault();
    const dropEffect = event.dataTransfer!.dropEffect = getDropEffect()!;
    endLayerDrag(dropEffect);
    const layerGroupViewer = makeLayerGroupViewer();
    dropLayers.updateArchiveStates(event);
    for (const newLayer of dropLayers.layers.keys()) {
      layerGroupViewer.layerSpecification.add(newLayer);
    }
    try {
      layerGroupViewer.restoreState(layoutSpec);
    } catch {
      layerGroupViewer.layout.reset();
      // Ignore error restoring layout.
    }
  });
}

export class StackLayoutComponent extends RefCounted implements LayoutComponent {
  changed = new NullarySignal();

  get length() {
    return (this.element.childElementCount - 1) / 2;
  }

  private makeDropPlaceholder(refCounted: RefCounted) {
    const dropZone = document.createElement('div');
    dropZone.className = 'neuroglancer-stack-layout-drop-placeholder';
    dropZone.style.display = 'none';
    setupDropZone(dropZone, this.viewer.layerSpecification, () => {
      const nextElement = dropZone.nextElementSibling;
      let nextChild: LayoutComponentContainer|undefined;
      if (nextElement !== null) {
        nextChild = LayoutComponentContainer.getFromElement(nextElement);
      }
      const newChild = this.insertChild({type: 'viewer', layers: []}, nextChild);
      return <LayerGroupViewer>newChild.component;
    }, this.direction === 'row' ? 'column' : 'row');
    refCounted.registerDisposer(() => {
      removeFromParent(dropZone);
    });
    return dropZone;
  }

  get viewer() {
    return this.container.viewer;
  }

  constructor(
      public element: HTMLElement, public direction: 'row'|'column', children: any[],
      public container: LayoutComponentContainer) {
    super();
    element.classList.add('neuroglancer-stack-layout');
    element.classList.add(`neuroglancer-stack-layout-${direction}`);
    element.style.display = 'flex';
    element.style.flexDirection = direction;
    element.appendChild(this.makeDropPlaceholder(this));
    for (const childSpec of children) {
      this.insertChild(childSpec);
    }
  }

  get(index: number) {
    return LayoutComponentContainer.getFromElement(this.element.children[index * 2 + 1]);
  }

  insertChild(spec: any, before?: LayoutComponentContainer) {
    const child = new LayoutComponentContainer(this.viewer, spec, this);
    const dropZone = this.makeDropPlaceholder(child);
    child.element.classList.add('neuroglancer-stack-layout-child');
    // UMAP增加样式
    // if (spec.panelType === 'UMAP') {
    //   child.element.classList.add('neuroglancer-stack-layout-child-umap')
    // }
    child.registerDisposer(child.changed.add(this.changed.dispatch));
    child.registerDisposer(() => {
      this.element.removeChild(child.element);
      this.changed.dispatch();
    });
    const beforeElement = before !== undefined ? before.element : null;
    this.element.insertBefore(child.element, beforeElement);
    this.element.insertBefore(dropZone, beforeElement);
    this.changed.dispatch();
    return child;
  }

  disposed() {
    this.clear();
    super.disposed();
  }

  clear() {
    while (this.length !== 0) {
      this.get(0).dispose();
    }
  }

  * [Symbol.iterator]() {
    const {length} = this;
    for (let i = 0; i < length; ++i) {
      yield this.get(i);
    }
  }

  toJSON() {
    return {
      type: this.direction,
      children: Array.from(this).map(x => x.toJSON()),
    };
  }
}

function makeComponent(container: LayoutComponentContainer, spec: any) {
  const element = document.createElement('div');
  element.style.flex = '1';
  element.style.width = '0px';

  if (typeof spec === 'string') {
    if (container.parent !== undefined) {
      throw new Error(`Invalid layout component specification: ${JSON.stringify(spec)}`);
    }
    return new SingletonLayerGroupViewer(element, spec, container.viewer);
  }

  verifyObject(spec);
  const componentType = verifyObjectProperty(spec, 'type', verifyString);
  switch (componentType) {
    case 'row':
    case 'column': {
      return new StackLayoutComponent(
          element, componentType, verifyObjectProperty(spec, 'children', x => {
            const children = parseArray(x, y => y);
            if (container.parent === undefined && children.length === 0) {
              throw new Error('Stack layout requires at least one child.');
            }
            return children;
          }), container);
    }
    case 'viewer': {
      const viewer = container.viewer;
      const layerSpecification = new LayerSubsetSpecification(viewer.layerSpecification.addRef());
      const layerGroupViewer = new LayerGroupViewer(
          element, {
            display: viewer.display,
            layerSpecification,
            ...getCommonViewerState(viewer),
          },
          {
            showLayerPanel: viewer.uiControlVisibility.showLayerPanel,
            showViewerMenu: true,
            showLayerHoverValues: viewer.uiControlVisibility.showLayerHoverValues
          });
      try {
        layerGroupViewer.restoreState(spec);
      } catch (e) {
        layerGroupViewer.dispose();
        throw e;
      }
      return layerGroupViewer;
    }
    default: {
      // Treat it as a singleton layer group.
      return new SingletonLayerGroupViewer(element, spec, container.viewer);
    }
  }
}

export class RootLayoutContainer extends RefCounted implements Trackable {
  container = this.registerDisposer(
      new LayoutComponentContainer(this.viewer, this.defaultSpecification, undefined));

  get changed() {
    return this.container.changed;
  }

  get element() {
    return this.container.element;
  }

  constructor(public viewer: Viewer, public defaultSpecification: any) {
    super();
  }

  reset() {
    this.container.setSpecification(this.defaultSpecification);
  }

  restoreState(obj: any) {
    this.container.setSpecification(obj);
  }

  disposed() {
    super.disposed();
  }

  toJSON() {
    return this.container.toJSON();
  }
}

function getImagename(name: string){
  if(name.toLocaleLowerCase().includes('tissuemask')){
    return name.replace('TissueMask', i18n.layerPanelTissueMask)
  }else if(name.toLocaleLowerCase().includes('cellmask')){
    return name.replace('CellMask', i18n.layerPanelCellMask)
  }else {
    return name.replace('Image', i18n.layerPanelImage)
  }
}